import { DeliveryType } from "@omni/types/api/product/bff/shared/enums/deliveryType.enum";
import { debounce } from "@omni/utils/function.utils";
import React, { useEffect, useState } from "react";

import { AmountFieldComponent } from "@/components/Form/RHF/FormFields/AmountField/amountField.component";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { Config } from "@/utils/config.utils";

export type AmountInputComponentProps = {
    onQuantityChange: (newQuantity: number, oldQuantity: number) => void;
    value: number;
    roundingValue?: number;
    minimumOrderQuantity: number;
    activeDelivery?: DeliveryType;
    isFree?: boolean;
    isFreeEntryAddedByCustomer?: boolean;
    isUpdatingCart: boolean;
    hasProductMultipleCartEntries?: boolean;
};

const debounceChange = debounce(
    (newQuantity: number, oldQuantity: number, handleChange: Function) => {
        handleChange(newQuantity, oldQuantity);
    },
    500
);

const AmountInputComponent = ({
    onQuantityChange,
    value,
    roundingValue,
    minimumOrderQuantity,
    activeDelivery,
    isFree,
    isFreeEntryAddedByCustomer,
    isUpdatingCart,
    hasProductMultipleCartEntries
}: AmountInputComponentProps) => {
    const { t } = useGranite();

    const maximumOrderQuantity = Config.general["maximum-order-quantity"];
    const defaultRoundingValue: number = 1;
    const [quantity, setQuantity] = useState(0);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        setQuantity(value || 0);
    }, []);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (!isUpdatingCart) {
            setQuantity(value || 0);
        }
    }, [isUpdatingCart]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (!isUpdatingCart && value !== quantity) {
            setQuantity(value || 0);
        }
    }, [value]);

    const determineRoundingValue = () => {
        if (activeDelivery !== DeliveryType.ClickAndCollect) {
            return roundingValue ? roundingValue : 0;
        } else {
            return defaultRoundingValue;
        }
    };

    const onDecrease = () => {
        const newQuantity = quantity - determineRoundingValue();

        if (isUpdatingCart || newQuantity < minimumOrderQuantity) {
            return;
        }

        setQuantity(newQuantity);
        debounceChange(newQuantity, value, onQuantityChange);
    };

    const onIncrease = () => {
        const newQuantity = quantity + determineRoundingValue();

        if (isUpdatingCart || newQuantity > maximumOrderQuantity) {
            return;
        }

        setQuantity(newQuantity);
        debounceChange(newQuantity, value, onQuantityChange);
    };

    const onInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const newQuantity = parseInt(e.target.value, 10);
        setQuantity(newQuantity);

        if (
            isUpdatingCart ||
            Number.isNaN(newQuantity) ||
            newQuantity < minimumOrderQuantity ||
            newQuantity > maximumOrderQuantity
        ) {
            return;
        }

        debounceChange(newQuantity, value, onQuantityChange);
    };

    const onInputBlur = () => {
        if (quantity < minimumOrderQuantity || Number.isNaN(quantity)) {
            setQuantity(minimumOrderQuantity);
            debounceChange(minimumOrderQuantity, value, onQuantityChange);
        }

        if (quantity > maximumOrderQuantity) {
            setQuantity(maximumOrderQuantity);
            debounceChange(maximumOrderQuantity, value, onQuantityChange);
        }
    };

    const decreaseDisabled =
        quantity - determineRoundingValue() < minimumOrderQuantity ||
        isUpdatingCart;

    const increaseDisabled =
        quantity + determineRoundingValue() > maximumOrderQuantity ||
        isUpdatingCart;

    const isQuantityEditable =
        !isFree ||
        (isFreeEntryAddedByCustomer && !hasProductMultipleCartEntries);

    return (
        <AmountFieldComponent
            quantityEditable={!!isQuantityEditable}
            decreaseDisabled={decreaseDisabled}
            increaseDisabled={increaseDisabled}
            value={quantity}
            disabled={!isQuantityEditable || isUpdatingCart}
            min={minimumOrderQuantity}
            max={maximumOrderQuantity}
            aria-label={t("accessibility.ecommerce.amount.input.label")}
            onDecrease={onDecrease}
            onIncrease={onIncrease}
            onChange={onInputChange}
            onBlur={onInputBlur}
        />
    );
};

export { AmountInputComponent };
